import LogtoClient, {
  LogtoRequestError,
  SignInOptions as LogtoSignInOptions,
  UserInfoResponse,
} from "@logto/browser";
import { useState, createContext, useContext, useMemo, useRef, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AccessToken, decodeToken } from "~/lib/tokens";
import { clearPostHogIdentification } from "~/lib/posthog.client";

// Query keys for auth
export const authQueryKeys = {
  all: ["auth"] as const,
  auth: (orgId?: string | null) =>
    [...authQueryKeys.all, "auth", orgId] as const,
};

type SignInOptions = Omit<LogtoSignInOptions, "redirectUri">;

// Auth data interface
interface AuthData {
  user: UserInfoResponse | null;
  claims: AccessToken | null;
  isAuthenticated: boolean;
  accessToken: string | null;
  activeOrg: string | null;
}

export const SIGNOUT_RETURN_KEY = "signout_return_to";

export interface AuthContextType {
  user: UserInfoResponse | null;
  claims: AccessToken | null;
  isAuthenticated: boolean;
  accessToken: string | null;
  activeOrg: string | null;
  logtoClient: LogtoClient;
  isLoading: boolean;
  signOut: (redirectTo?: string) => Promise<void>;
  signIn: (options?: SignInOptions) => Promise<void>;
  refresh: () => Promise<void>;
  setActiveOrg: (orgId: string | null) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};

function syncSessionStorageOrg(orgId: string | null) {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  if (orgId === null) {
    sessionStorage.removeItem("activeOrgId");
  } else {
    sessionStorage.setItem("activeOrgId", orgId);
  }
}

// provider
export const AuthProvider = ({
  logtoClient,
  children,
}: {
  logtoClient: LogtoClient;
  children: React.ReactNode;
}) => {
  const [activeOrg, setActiveOrgState] = useState<string | null>(null);
  const activeOrgRef = useRef<string | null>(null);
  const queryClient = useQueryClient();
  const switchVersionRef = useRef(0);

  const refresh = useCallback(async () => {
    await queryClient.refetchQueries({
      queryKey: authQueryKeys.auth(activeOrgRef.current ?? undefined),
    });
  }, [queryClient]);

  const signOut = useCallback(async (redirectTo?: string) => {
    clearPostHogIdentification();
    if (redirectTo && typeof window !== "undefined") {
      sessionStorage.setItem(SIGNOUT_RETURN_KEY, redirectTo);
    }
    await logtoClient.signOut(
      new URL("/callback/signout", window.location.origin).toString(),
    );
  }, [logtoClient]);

  const signIn = useCallback(async (options?: SignInOptions) => {
    // Where to come back to. The callback already restores this key; without
    // something writing it, every sign-in landed on home — which throws away a
    // deep link, and with it the `?org=` hint that sent somebody to a
    // particular org in the first place.
    if (typeof window !== "undefined") {
      sessionStorage.setItem(
        "post_signin_redirect_uri",
        window.location.pathname + window.location.search,
      );
    }
    await logtoClient.signIn({
      ...(options ?? {}),
      redirectUri: new URL("/callback", window.location.origin).toString(),
    });
  }, [logtoClient]);

  const setActiveOrg = useCallback(async (orgId: string | null) => {
    if (orgId === activeOrgRef.current) {
      return;
    }

    const version = ++switchVersionRef.current;

    syncSessionStorageOrg(orgId);
    activeOrgRef.current = orgId;
    setActiveOrgState(orgId);

    await queryClient.invalidateQueries({
      queryKey: authQueryKeys.auth(orgId ?? undefined),
    });

    if (version !== switchVersionRef.current) return;

    queryClient.removeQueries({
      queryKey: authQueryKeys.all,
      predicate: (query) => {
        const key = query.queryKey;
        return key[2] !== (orgId ?? undefined);
      },
    });
  }, [queryClient]);

  const fetchAuthData = useCallback(async (
    orgIdParam: string | null,
  ): Promise<AuthData> => {
    const isAuthenticated = await logtoClient.isAuthenticated();

    if (!isAuthenticated) {
      return {
        user: null,
        claims: null,
        isAuthenticated: false,
        accessToken: null,
        activeOrg: null,
      };
    }

    let token: string | null = null;
    let effectiveOrgId: string | null = orgIdParam;
    try {
      token = await logtoClient.getAccessToken(
        logtoClient.logtoConfig.resources![0]!,
        effectiveOrgId ?? undefined,
      );
    } catch (error) {
      if (error instanceof LogtoRequestError) {
        if (effectiveOrgId != null && error.code === "oidc.access_denied") {
          effectiveOrgId = null;
          syncSessionStorageOrg(null);
          activeOrgRef.current = null;

          token = await logtoClient.getAccessToken(
            logtoClient.logtoConfig.resources![0]!,
            undefined,
          );
        } else {
          throw error;
        }
      } else {
        throw error;
      }
    }

    const claims = decodeToken(token);
    const scopes = claims.scope?.split(" ") ?? [];
    const mergedClaims = { ...claims, scopes };
    const user = await logtoClient.fetchUserInfo();

    return {
      user: user ?? null,
      claims: mergedClaims,
      isAuthenticated: true,
      accessToken: token ?? null,
      activeOrg: effectiveOrgId,
    };
  }, [logtoClient]);

  // React Query hook for auth data -- orgId comes from the query key, not closure
  const authQuery = useQuery({
    queryKey: authQueryKeys.auth(activeOrg ?? undefined),
    queryFn: ({ queryKey }) => {
      const orgIdFromKey = (queryKey[2] as string | undefined) ?? null;
      return fetchAuthData(orgIdFromKey);
    },
    enabled: !!logtoClient,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const authState: AuthContextType = useMemo(() => ({
    user: authQuery.data?.user ?? null,
    claims: authQuery.data?.claims ?? null,
    isAuthenticated: authQuery.data?.isAuthenticated ?? false,
    accessToken: authQuery.data?.accessToken ?? null,
    activeOrg: authQuery.data?.activeOrg ?? null,
    logtoClient: logtoClient,
    isLoading: authQuery.isLoading,
    refresh,
    signOut,
    signIn,
    setActiveOrg,
  }), [authQuery.data, authQuery.isLoading, logtoClient, refresh, signOut, signIn, setActiveOrg]);

  return (
    <AuthContext.Provider value={authState}>{children}</AuthContext.Provider>
  );
};
